/* global SunergoPlatform */
import axios from 'axios';

// remove trailing slash if it exists
let currentPath = window.location.pathname;
currentPath = currentPath.endsWith('/') ? currentPath.slice(0, -1) : currentPath;

var labelSelectTimes;
var inputSelectDate;
var flatpickr;

var i18n = {};

/**
 * Selected Time Slots are stored in this object.
 * Handles adding, removing, and displaying time slots selected.
 */
const selectedTimeslots = {
	form: false,
	items: {},
	add(id, data) {
		this.items[id] = data;
		this.showSelected();
	},
	remove(id) {
		delete this.items[id];
		this.showSelected();
	},
	removeAll() {
		this.items = {};
	},
	showSelected() {
		// Check if form is available.
		if (!this.form) return;

		// Update the form note with the number of selected time slots.
		const numSelected = Object.keys(this.items).length;
		let output = `${numSelected} `;

		const style = availabilityData ? availabilityData.style : false;
		if (style) {
			if (style == 'day') output += `${i18n.day} `;
			else output += `${i18n.time} `;
		}

		if (numSelected == 1) output += i18n.slot;
		else output += i18n.slots;
		this.form.processing.setNote(`${output} ${i18n.selected}`);

		if (flatpickr) flatpickr.redraw();
	},
	hasDate(date) {
		for (let id in this.items) {
			const itemDate = new Date(this.items[id].utc_start);
			if (date && date.getFullYear() == itemDate.getFullYear() && date.getMonth() == itemDate.getMonth() && date.getDate() == itemDate.getDate()) return true;
		}
		return false;
	}
};

/**
 * Get previous and next month year/date.
 * @param {object} currentItem - the current date item.
 * @param {object} currentItem.year - the current year.
 * @param {object} currentItem.month - the current month.
 * @returns {object[]} array of months.
 */
const getAdjacentMonths = function(currentItem) {
	const year = currentItem.year,
		month = currentItem.month,
		adjacentMonths = [];

	// Validate current month.
	if (month < 1 || month > 12) {
		console.error('[Facility Availability] Month out of range');
		return false;
	}

	// Get previous month.
	const prevMonth = { year: year, month: month - 1 };
	if (prevMonth.month < 1) {
		prevMonth.month = 12;
		prevMonth.year = prevMonth.year - 1;
	}
	adjacentMonths.push(prevMonth);

	// Get next month.
	const nextMonth = { year: year, month: month + 1 };
	if (nextMonth.month > 12) {
		nextMonth.month = 1;
		nextMonth.year = nextMonth.year + 1;
	}
	adjacentMonths.push(nextMonth);

	// Add current month.
	adjacentMonths.push(currentItem);

	return adjacentMonths;
};

const availabilityData = {};

/**
 * Get availability data for a year, month, and date.
 * @param {number} year - UTC year.
 * @param {number} month - UTC month (Jan = 1).
 * @param {number} date - UTC date.
 * @returns the availability data for the specified date.
 */
const getAvailabilityData = function(year, month, date) {
	if (!availabilityData || !year) return false;
	let data = availabilityData[year];
	if (month && month > 0) data = data ? data[month] : false;
	if (date && date > 0) data = data ? data[date][0] : false;
	if (!data) return false;
	else return data;
};

/**
 * Check if there is data for the month provided.
 * @param {number} year - Year of data.
 * @param {number} month - Month of data.
 * @returns {boolean} true when there is data.
 */
const hasMonthData = function(year, month) {
	if (availabilityData == undefined) return false;
	else if (availabilityData[year] == undefined) return false;
	else if (availabilityData[year][month] == undefined) return false;
	else return true;
};

/**
 * Get availability data from server.
 * @param {object} params - parameters.
 * @param {object} params.year - Year of data.
 * @param {number} params.month - Month of data.
 * @param {number} params.date - Date of data (optional).
 * @param {object} params.calendar - Flatpickr object (optional).
 * @returns error if unable to find data.
 */
const getData = async function(params) {
	const year = params.year,
		month = params.month,
		date = params.date || false,
		calendar = params.calendar || false,
		table = params.table || false;

	if (!year || !month)
		return console.error('[Facility Availability] Get Data stopped: missing year or month.');

	// Request data.
	const requestUrl = currentPath + '/get/' + year + '/' + month;
	let success = false;
	await axios
		.get(requestUrl, {
			timeout: 5000,
			responseType: 'json'
		})
		.then(function(res) {
			// Set the availability style.
			availabilityData.style = res.data.style;

			// If the year wasn't loaded before, create the object now.
			if (availabilityData[year] === undefined) availabilityData[year] = [];

			// Save the timeblock data.
			availabilityData[year][month] = res.data.timeblocks;
			success = true;
		})
		.catch(function(error) {
			console.error('[Facility Availability]', error);
		});
	return success;
};

/**
 * Get availability data for month.
 * @param {object} params - parameters to load month.
 * @param {number} params.year - year of month to load.
 * @param {number} params.month - month to load.
 * @param {object} params.calendar - Flatpickr object (optional).
 * @param {object} params.table - Tabulator object (optional).
 * @returns {boolean} true if getting data (asynchronous task will handle Flatpickr and Tabulator updates).
 */
const getMonthAvailability = async function(params) {
	const months = getAdjacentMonths(params);
	if (!months) return;

	let gettingData = false;
	let hasData = true;
	for (let i = 0; i < months.length; i++) {
		const item = months[i];

		if (!hasMonthData(item.year, item.month)) {
			if (gettingData == false) gettingData = item.calendar || item.table ? true : false;

			hasData = await getData({
				year: item.year,
				month: item.month,
				date: item.date || false,
				calendar: item.calendar || false,
				table: item.table || false
			});
		}
	}

	if (params.calendar) updateFlatpickr(params.calendar, params.year, params.month);
	return { gettingData, hasData };
};

/**
 * Get a 2-digit number for use in JS Dates.
 * @param {number} num - 1 or 2 digit number.
 * @returns {number} a 2-digit number.
 */
const twoDigit = function(num) {
	return num.toString().padStart(2, '0');
};

/**
 * Check if date is valid.
 * @param {number} year - year
 * @param {number} month - month
 * @param {number} date - date
 * @returns {boolean} true if date is valid.
 */
const isValidDate = function(year, month, date) {
	return !isNaN(new Date(year + '-' + twoDigit(month) + '-' + twoDigit(date) + 'T00:00:00'));
};

/**
 * Get availability data for specific date.
 * @param {object} params - parameters to load month.
 * @param {number} params.year - year of date to load.
 * @param {number} params.month - month of date to load.
 * @param {number} params.date - date to load.
 * @param {Date} params.selectedDate - date to load.
 * @param {object} params.spTable - Tabulator object (optional).
 * @returns early if date is not valid.
 */
const getDateAvailability = async function(params) {
	SunergoPlatform.loading.add('getting-availability');

	if (params.selectedDate) {
		params.year = params.selectedDate.getFullYear();
		params.month = params.selectedDate.getMonth() + 1;
		params.date = params.selectedDate.getDate();
	}

	const year = params.year,
		month = params.month,
		date = params.date;

	// Validate date.
	if (!isValidDate(year, month, date))
		return console.error('[Facility Availability] Date out of range');

	// If data not available, load it in.
	const { hasData } = await getMonthAvailability(params);
	if (hasData) updateTable(params.spTable, availabilityData[year][month][date]);
	else updateTable(params.spTable, false, true);

	SunergoPlatform.loading.remove('getting-availability');
};

const updateTable = function(table, data, error) {
	if (!table) return console.warn('[Facility Availability] Table not loaded');

	// If not data is provided, empty the table (placeholder appears).
	// Otherwise, set data and setup checkboxes.
	if (!data) table.remote.clearTable(error ? 'error' : '');
	else table.remote.setData({ rows: data, numRows: data.length });

	// Show date in table label.
	labelSelectTimes.textContent = `${labelSelectTimes.dataset.labelSelectTimes} ${inputSelectDate.value}`;
};

/**
 * Enable calendar dates based on availability.
 * @param {number} year - Year of data.
 * @param {number} month - Month of data.
 * @returns {Date[]} array of enabled dates.
 */
const enabledDates = function(year, month) {
	const enabledDates = [];

	// Check current month, as well as previous and next months (these are visible on calendar).
	const months = getAdjacentMonths({ year, month });
	for (let i = 0; i < months.length; i++) {
		const item = months[i];
		if (!hasMonthData(item.year, item.month)) continue;

		// For the current month, get the availability and check which days have availability.
		const currMonth = availabilityData[item.year][item.month];
		for (let date = 1; date < currMonth.length; date++) {
			if (currMonth[date].length > 0 && isValidDate(item.year, item.month, date)) {
				enabledDates.push(new Date(item.year, item.month - 1, date));
			}
		}
	}
	return enabledDates;
};

// let updatingFlatpickr = false;
const updatingFlatpickr = {
	container: false,
	isUpdating: false,
	get status() {
		return this.isUpdating;
	},
	set status(state) {
		this.container.dataset.updating = state;
		this.isUpdating = state;
	}
};

/**
 * Update Flatpickr instance.
 * @param {object} calendar - Flatpickr object.
 * @param {number} year - selected year in Flatpickr.
 * @param {number} month - selected month in Flatpickr.
 */
const updateFlatpickr = function(calendar, year, month) {
	calendar.set('enable', enabledDates(year, month));
	updatingFlatpickr.status = false;
};

/**
 * Initialize pre-submission task for Availability Form.
 */
const initPreSubmit = function(form) {
	// Save the form for use with time slot note.
	selectedTimeslots.form = form;

	/**
	 * Setup the pre-submit task.
	 * @returns {boolean} true when time blocks are added, false when there are no time blocks.
	 */
	const availabilityPreSubmit = function() {
		const timeblocks = Object.values(selectedTimeslots.items);

		// If there are no time blocks selected, we can't submit.
		if (!timeblocks || timeblocks.length < 1) {
			SunergoPlatform.feedbackBanner.show({
				id: 'noTimeblocks',
				type: 'info',
				content: i18n.feedbackNoTimeblocks
			});
			return false;
		}

		// Prepare data for submission.
		const data = timeblocks.map(function(slot) {
			return {
				FACILITYAVAILABILITY_ID: slot.FACILITYAVAILABILITY_ID,
				utc_start: slot.utc_start,
				utc_end: slot.utc_end
			};
		});

		// Find the hidden selected time block holder field and insert values.
		const timeblockField = form.element.querySelector('[name="selectedTimeblocks"]');
		timeblockField.value = JSON.stringify(data);
		return true;
	};

	// Add this task to the pre-submission task list.
	form.preSubmit.push(availabilityPreSubmit);
};

SunergoPlatform.onLoad.push(async function() {
	// Get the availability form.
	const form = SunergoPlatform.forms['availability'];
	if (!form) return console.error('[Facility Availability] Form not found');

	// Set variables.
	labelSelectTimes = document.querySelector('[data-label-select-times]');
	inputSelectDate = document.querySelector('[name="selectDate"]').parentElement.querySelector('.form-control');

	i18n = {
		feedbackNoTimeblocks: form.element.dataset.feedbackNoTimeblocks,
		day: form.element.dataset.i18nDay,
		time: form.element.dataset.i18nTime,
		slot: form.element.dataset.i18nSlot,
		slots: form.element.dataset.i18nSlots,
		selected: form.element.dataset.i18nSelected,
		hasTimeSlotsSelected: form.element.dataset.i18nHasTimeSlotsSelected
	};

	initPreSubmit(form);

	// Retrieve Flatpickr instance.
	const flatpickrContainer = document.getElementById('availability_selectDate');
	flatpickr = flatpickrContainer._flatpickr;
	if (!flatpickr) return console.error('[Facility Availability] Flatpickr not loaded.');

	updatingFlatpickr.container = flatpickrContainer.parentElement;
	updatingFlatpickr.status = true;

	// Retrieve Table instance.
	const table = SunergoPlatform.tableList['availability'];

	// Get Selected Dates, if already selected in Flatpickr (ie. cached).
	// Otherwise, get today (and this month's) availability.
	const selectedDate =
		flatpickr.selectedDates && flatpickr.selectedDates.length > 0
			? flatpickr.selectedDates[0]
			: new Date();

	// Add cost per date for Day style facilities.
	flatpickr.config.onDayCreate = [
		function(dObj, dStr, fp, dayElem) {
			const date = dayElem.dateObj;
			const dateObject = {
				year: date.getUTCFullYear(),
				month: date.getUTCMonth() + 1,
				date: date.getUTCDate()
			};
			const data = getAvailabilityData(dateObject.year, dateObject.month, dateObject.date);

			dayElem.innerHTML = `${dateObject.date}`;

			if (availabilityData && availabilityData.style == 'day') {
				if (data && data.cost) {
					const costAmount = parseFloat(data.cost).toFixed(2);
					dayElem.innerHTML += `<small class="facility-availability__cost">$${costAmount}</small>`;
				}
			} else {
				// If the date has selected timeslots, show an indicator.
				if (selectedTimeslots.hasDate(date)) {
					dayElem.innerHTML += `<span class="flatpickr-day__marker" title="${i18n.hasTimeSlotsSelected}"><span class="visually-hidden">${i18n.hasTimeSlotsSelected}</span></span>`;
				}
			}
		}
	];

	// Update Table whenever flatpickr date is selected.
	flatpickr.config.onChange.push(function(selectedDates) {
		if (!selectedDates || selectedDates.length < 1) {
			if (availabilityData.style == 'day') selectedTimeslots.removeAll();
			selectedTimeslots.showSelected();
			return;
		}

		const selectedDate = selectedDates[0]; // Be careful in changing selectedDate; will change flatpickr.
		const year = selectedDate.getFullYear();
		const month = selectedDate.getMonth() + 1;
		const date = selectedDate.getDate();

		// After loading data (in case selection crosses month)
		if (availabilityData.style == 'day') {
			/*** Multi-Day Availability Style ***/
			// When a date range is selected, the time blocks that fit within the date range are automatically selected.
			// This is because Table is not shown, and so there would not be a place to select the specific time blocks.

			// Remove all previous selected time slots (only 1 date range is supported in Flatpickr).
			selectedTimeslots.removeAll();

			// Add each timeblock in range to selectedTimeblocks
			const block = availabilityData[year][month][date][0];
			selectedTimeslots.add(block.id, block);

			// Copy by value to temp variable to not affect Flatpickr.
			let currentDate = new Date(selectedDate.getTime()),
				endDate = null;
			currentDate = new Date(currentDate.setDate(currentDate.getDate() + 1)); // move date up
			if (selectedDates.length > 1) endDate = selectedDates[1];

			// Add all timeblocks in range.
			while (currentDate < endDate && endDate != null) {
				const currYear = currentDate.getFullYear();
				const currMonth = currentDate.getMonth() + 1;
				const currDate = currentDate.getDate();
				const currTimeblock = availabilityData[currYear][currMonth][currDate][0];
				selectedTimeslots.add(currTimeblock.id, currTimeblock);

				currentDate = new Date(currentDate.setDate(currentDate.getDate() + 1)); //increment to next date
			}
		} else {
			/*** Time Slot Availability Style ***/
			getDateAvailability({ selectedDate, spTable: table });
		}
	});

	/**
	 * Flatpickr onMonth and onYear Change listener.
	 * @param {*} selectedDates - Dates selected in Flatpickr.
	 * @param {*} dateStr - the Date String of the current date.
	 * @param {*} instance - Flatpickr instance.
	 * @returns if already updating Flatpickr.
	 */
	const onMonthYearChange = async function(selectedDates, dateStr, instance) {
		if (updatingFlatpickr.status) return;
		else updatingFlatpickr.status = true;

		const year = flatpickr.currentYear;
		const month = flatpickr.currentMonth + 1;
		await getMonthAvailability({ year, month, calendar: flatpickr });
	};

	// Update Flatpickr and Tabulator when months change.
	flatpickr.config.onMonthChange.push(onMonthYearChange);
	flatpickr.config.onYearChange.push(onMonthYearChange);

	// Add callback to SP Table selected updated.
	if (table && table.selection) {
		table.selection.callback.selectRow = function({ rowId, data, selected }) {
			if (selected) selectedTimeslots.add(rowId, data);
			else selectedTimeslots.remove(rowId);
		};
		table.selection.callback.initRow = function({ row, data }) {
			if (data.isBookable) return;
			const selected = data.nonBookableReason == 'In Your Cart';
			table.selection.disableRow({row, selected, label: data.nonBookableReason});
		};
	}

	// Get and display today's availability.
	await getDateAvailability({ selectedDate, calendar: flatpickr, spTable: table });
});
